import { FC, Suspense, useEffect, useRef, useState } from 'react';
import { DashboardFiltersQuery } from '../../graphQLOperations/__generated__/DashboardFiltersQuery.graphql';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectedTeacherDashboardFilter,
  setTeacherDashboardFilter,
} from '../../redux/reducers/teacherDashboardFiltersSlice';
import { useLazyLoadQuery } from 'react-relay';
import { getFiltersInformation, getTotalStudentsFiltered } from '../../graphQLOperations/Dashboard';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Card } from 'primereact/card';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputSwitch } from 'primereact/inputswitch';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { DashboardGetStudentsCountQuery } from '../../graphQLOperations/__generated__/DashboardGetStudentsCountQuery.graphql';
import { Tooltip } from 'primereact/tooltip';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';
import { DashboardStudentsRosterQuery$data } from '../../graphQLOperations/__generated__/DashboardStudentsRosterQuery.graphql';
import { ClassNameSection } from '../../models/Class';

interface Props {
  dismissGuide?: boolean;
  hasUserClassWithActiveStudent?: DashboardStudentsRosterQuery$data['hasUserClassWithActiveStudent'];
}

export const TeacherDashboardFilters: FC<Props> = ({ dismissGuide = true, hasUserClassWithActiveStudent }) => {
  const dispatch = useAppDispatch();
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const [classIndex, setClassIndex] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [multiselectEnabled, setMultiselectEnabled] = useState(false);
  const op = useRef<OverlayPanel>(null);
  const multiToggleBtnRef = useRef<HTMLButtonElement>(null);
  const { classes } = useLazyLoadQuery<DashboardFiltersQuery>(
    getFiltersInformation,
    {},
    { fetchPolicy: 'network-only' },
  );
  const hiddenClassIds = useHiddenClassIds();

  const orderedClasses: ClassNameSection[] = [...(classes?.nodes || [])].filter(cl => cl && !hiddenClassIds.has(cl.id));
  const hasMultiple = orderedClasses.length > 1;
  orderedClasses.sort((a, b) => (a.name! > b.name! ? 1 : -1));

  useEffect(() => {
    const current = orderedClasses[classIndex] ?? orderedClasses[0];
    dispatch(
      setTeacherDashboardFilter({
        ...teacherDashboardFilters,
        classes: current
          ? [
              {
                id: current.id || '',
                name: `${current.name}${current.section ? ` - ${current.section}` : ''}`,
              },
            ]
          : [],
      }),
    );
  }, [classIndex, resetKey, orderedClasses.length]);

  const onPageChange = (e: PaginatorPageChangeEvent) => {
    if (multiselectEnabled) return;
    setClassIndex(e.first);
  };

  const handleClassSelection = (id: string) => {
    const selectedClass = orderedClasses.find(cl => cl.id === id);
    const prevClasses = teacherDashboardFilters.classes || [];

    if (prevClasses.length === 2 && prevClasses.some(cls => cls.id === id)) {
      setMultiselectEnabled(false);
    }

    if (prevClasses.length === 1 && selectedClass?.id === prevClasses[0].id) {
      return;
    }

    if (prevClasses?.some(cl => cl.id === id)) {
      dispatch(
        setTeacherDashboardFilter({ ...teacherDashboardFilters, classes: prevClasses.filter(cl => cl.id !== id) }),
      );
    } else {
      dispatch(
        setTeacherDashboardFilter({
          ...teacherDashboardFilters,
          classes: [
            ...prevClasses,
            {
              id: selectedClass?.id || '',
              name: `${selectedClass?.name} ${selectedClass?.section ? ` - ${selectedClass.section}` : ''}`,
            },
          ],
        }),
      );
    }
  };

  const handleMultiselect = (state: boolean) => {
    if (!state) {
      setClassIndex(0);
      setResetKey(new Date().getTime());
    }
    setMultiselectEnabled(state);
  };
  const onPanelShow = () => {
    requestAnimationFrame(() => multiToggleBtnRef.current?.focus());
  };

  return (
    <Card
      className={`!tw-p-0 tw-mb-2 nc_teacher-dashboard-filters ${
        !hasUserClassWithActiveStudent ? '!tw-bg-neutral-100' : 'nc_block'
      }`}
      pt={{ content: { style: { paddingTop: '0', borderRadius: '1.25rem' } } }}
    >
      <div
        className={`tw-flex tw-items-center tw-w-full tw-pt-0 tw-my-2 lg:tw-mt-0 lg:tw-h-[4rem] ${
          dismissGuide ? 'tw-flex-row lg:tw-pl-3' : 'lg:tw-flex-row tw-flex-col tw-gap-y-2'
        }`}
      >
        {multiselectEnabled && (
          <Tooltip target={`.pagintator`} mouseTrack position="right">
            Disabled due to multiple classes selected
          </Tooltip>
        )}
        {!multiselectEnabled && (
          <Paginator
            className={`md:tw-pl-2 md:tw-pt-0 tw-m-0 tw-p-0 tw-text-neutral-950 ${
              hasUserClassWithActiveStudent ? '' : 'tw-bg-neutral-100'
            }`}
            first={classIndex}
            rows={1}
            totalRecords={hasUserClassWithActiveStudent ? orderedClasses.length || 0 : 0}
            onPageChange={onPageChange}
            template="PrevPageLink CurrentPageReport NextPageLink"
            pt={{
              current: {
                className: `tw-font-atkinson ${hasUserClassWithActiveStudent ? '' : '!tw-hidden'} `,
              },
            }}
          />
        )}
        <button
          id="classSelectorBtn"
          type="button"
          aria-haspopup="dialog"
          aria-controls="classSelectorPanel"
          onClick={e => op.current?.toggle(e)}
          className="tw-font-bold tw-text-xl text-primary tw-underline tw-cursor-pointer tw-mx-2 tw-font-atkinson
                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-400 tw-bg-transparent tw-border-none tw-p-0"
        >
          {teacherDashboardFilters.classes && teacherDashboardFilters.classes.length > 0
            ? multiselectEnabled
              ? 'Multiple classes selected'
              : teacherDashboardFilters.classes?.[0].name
            : ''}
        </button>

        {hasMultiple && (
          <OverlayPanel className="!tw-z-[2]" ref={op} onShow={onPanelShow} style={{ fontFamily: 'Poppins' }}>
            <div className="flex flex-column" role="dialog" aria-labelledby="classSelectorBtn">
              <button
                ref={multiToggleBtnRef}
                aria-pressed={multiselectEnabled}
                onClick={() => handleMultiselect(!multiselectEnabled)}
                className="w-full flex align-items-center border-bottom-1 pb-2 mb-1 surface-border
                         bg-transparent border-none text-left p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-400"
              >
                <span className="text-base font-semibold pt-1 mr-2">Select multiple classes</span>
                <InputSwitch
                  className="col-3 max-w-3rem"
                  checked={multiselectEnabled}
                  onChange={e => {
                    handleMultiselect(e.target.value);
                  }}
                  tabIndex={-1}
                />
              </button>
              {orderedClasses.map((cl, inx) => {
                const isChecked = teacherDashboardFilters.classes?.some(cls => cls.id === cl.id) || false;
                return (
                  <div key={cl.id} className="flex align-items-center">
                    {multiselectEnabled ? (
                      <>
                        <Checkbox
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleClassSelection(cl.id);
                            }
                          }}
                          onChange={() => handleClassSelection(cl.id)}
                          checked={isChecked}
                        />
                        <label
                          className="font-semibold block w-full my-2 cursor-pointer ml-2"
                          htmlFor={`class-${cl.id}`}
                        >
                          {cl.name} {cl.section ? ' - ' + cl.section : ''}
                        </label>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (multiselectEnabled) {
                            return;
                          }
                          setClassIndex(inx);
                        }}
                        className={`font-semibold block w-full my-2 text-left bg-transparent border-none p-0
                                  ${inx !== classIndex ? 'text-primary cursor-pointer' : ''}
                                  focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-400`}
                        aria-current={inx === classIndex ? 'true' : undefined}
                      >
                        {cl.name} {cl.section ? ' - ' + cl.section : ''}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </OverlayPanel>
        )}
        <span className="tw-ml-2 tw-font-atkinson tw-text-neutral-900">
          {' '}
          <Suspense>
            {hasUserClassWithActiveStudent ? (
              <>
                {'('} <TotalStudents />
                {' students)'}
              </>
            ) : (
              <div className="tw-flex tw-flex-row tw-gap-x-4 tw-items-center">
                <h1 className="tw-font-atkinson tw-text-neutral-600 tw-text-2xl tw-font-bold">No classes yet</h1>
                <span className="tw-font-atkinson tw-text-neutral-600">0 students</span>
              </div>
            )}
          </Suspense>
        </span>
      </div>
      {multiselectEnabled && (
        <div className="tw-flex tw-mb-4 tw-ml-4 tw-self-start grid gap-2">
          {teacherDashboardFilters.classes?.map(cl => (
            <Button
              key={cl.id}
              className="mx-1 h-2rem"
              icon="pi pi-times-circle"
              style={{ fontFamily: 'Poppins' }}
              iconPos="right"
              outlined
              label={cl.name}
              onClick={() => handleClassSelection(cl.id)}
            ></Button>
          ))}
          <Button
            label="Reset all"
            outlined
            className="border-2 ml-2 h-2rem"
            onClick={() => handleMultiselect(false)}
          />
        </div>
      )}
    </Card>
  );
};

const TotalStudents: FC = () => {
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const { allStudents } = useLazyLoadQuery<DashboardGetStudentsCountQuery>(
    getTotalStudentsFiltered,
    {
      filter: {
        siteIds: null,
        classIds: teacherDashboardFilters.classes?.length
          ? teacherDashboardFilters.classes.map(cl => cl.id)
          : null /*teacherDashboardFilters.classesIdsDefaultValue*/,
        gradeLevelIds: teacherDashboardFilters.grades?.length ? teacherDashboardFilters.grades?.map(gd => gd.id) : null,
      },
      skip:
        !teacherDashboardFilters.classes?.length &&
        !teacherDashboardFilters.grades?.length &&
        !teacherDashboardFilters.sites?.length,
    },
    { fetchPolicy: 'network-only' },
  );
  return <span className="tw-font-atkinson tw-text-neutral-950">{allStudents?.totalCount}</span>;
};
